import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  requestListNotification,
  requestUpdateNotification,
  useAuthState,
  useNotificationDispatch,
  useNotificationState,
} from "../../Context";
import { BellIcon } from "@heroicons/react/solid";
import {
  classNames,
  isLastTokenNull,
  notificationType,
  pathBuilder,
} from "../../Utils";
import { useNavigate } from "react-router-dom";
import { Pagination } from "../../Components/Pagination";
import { EmptyList } from "../../Components/EmptyList";
import { Alert } from "../../Components/Alert";
import { useTranslation } from "react-i18next";

const startOfLast7Days = dayjs().subtract(7, "day").toISOString();
const endOfToday = dayjs().endOf("day").toISOString();

export const Notification = () => {
  const dispatch = useNotificationDispatch();
  const { notifications, nextTokens, loading, error } = useNotificationState();

  const { user } = useAuthState();

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [page, setpage] = useState(1);

  useEffect(() => {
    if (user.id) {
      requestListNotification(dispatch, {
        limit: 10,
        page: 1,
        owner: user.id,
        createdAt: {
          between: [startOfLast7Days, endOfToday],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage] && user.id) {
      requestListNotification(dispatch, {
        limit: 10,
        page: nextPage,
        owner: user.id,
        createdAt: {
          between: [startOfLast7Days, endOfToday],
        },
        nextToken: nextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0 && user.id) {
      requestListNotification(dispatch, {
        limit: 10,
        page: prevPage,
        owner: user.id,
        nextToken: nextTokens[prevPage],
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      if (user.id) {
        requestListNotification(dispatch, {
          limit: 10,
          page: selectedPage,
          owner: user.id,
          nextToken: nextTokens[selectedPage],
        });
      }
    }
  };

  function renderNotificationList() {
    if (loading) {
      return (
        <ul>
          {[...Array(3)].map((_, i) => (
            <li key={i} className="px-4 py-3 animate-pulse">
              <div className="h-3 w-24 sm:w-[25%] mt-1 mb-3 bg-gray-300 rounded-md"></div>
              <div className="h-3 w-40 sm:w-[50%] my-1 bg-gray-300 rounded-md"></div>
              <div className="h-3 w-40 sm:w-[50%] my-1 bg-gray-300 rounded-md"></div>
            </li>
          ))}
        </ul>
      );
    }

    if (error) {
      return (
        <div className="px-4 py-3">
          <Alert
            title="Error"
            type="error"
            message={error?.message || "Error fetching notification list."}
            errors={error?.errors}
          />
        </div>
      );
    }

    if (notifications.length === 0) {
      return (
        <div className="px-4 py-3">
          <EmptyList
            title={
              isLastTokenNull(nextTokens)
                ? "No Notification"
                : "Please click Next again to load the data"
            }
          />
        </div>
      );
    }

    return (
      <ul>
        {notifications.map((notification, index) => {
          let type = notification.type || "notification";
          type = type.toLowerCase();
          const path = pathBuilder(notification.type, notification.typeId);
          const Icon = notificationType[type]?.icon || BellIcon;

          return (
            <li key={notification.id}>
              <a
                href={path || "#"}
                onClick={async (e) => {
                  e.preventDefault();
                  if (!notification.isRead) {
                    await requestUpdateNotification(dispatch, {
                      id: notification.id,
                      isRead: true,
                    });
                  }
                  if (path) {
                    navigate(path);
                  }
                }}
              >
                <div
                  className={classNames(
                    !notification.isRead && "bg-blue-100",
                    index === 0 && "rounded-t-md sm:rounded-t-lg",
                    "px-4 py-3 hover:bg-slate-100"
                  )}
                >
                  <div className="flex flex-wrap items-center text-sm text-gray-500 mb-2">
                    <Icon
                      className={classNames(
                        notificationType[type]?.color || "text-blue-500",
                        "h-5 w-5 mr-2"
                      )}
                    />
                    <span className="mr-2">
                      {t(notificationType[type]?.title) || t("Notification")}
                    </span>
                    <span>•</span>
                    <span className="ml-2">
                      {dayjs(notification.createdAt)
                        .locale(i18n.language)
                        .format("DD MMM YYYY")}
                    </span>
                  </div>
                  <p className="font-bold">{notification.title}</p>
                  <p className="line-clamp-2">{notification.description}</p>
                </div>
              </a>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="bg-white min-h-full shadow-sm rounded-md sm:rounded-lg divide-y divide-gray-200">
      {renderNotificationList()}
      <Pagination
        className="rounded-b-md sm:rounded-b-lg"
        page={page}
        onPrev={onPrev}
        onNext={onNext}
        onPageChange={onPageChange}
        nextTokens={nextTokens}
      />
    </div>
  );
};
